/** Simple recurrence (what the editor writes): description and building from the editor's choice. */
import type { Translate } from '@platform/i18n';
import type { CalendarEvent } from '../api';
import type { CalendarFormat } from './format';

export type Recurrence = NonNullable<CalendarEvent['recurrence']>;
export type WeekIndex = NonNullable<Recurrence['weekIndex']>;
export type RepeatChoice = 'none' | 'daily' | 'weekdays' | 'weekly' | 'monthly' | 'yearly';

export const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;

export const isoDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Which week of its month a date is in ("2nd Tuesday"); `last`: also the last such weekday when it is. */
export function nthOf(date: Date, preferLast = false): WeekIndex {
  const daysInMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  if (preferLast && date.getDate() + 7 > daysInMonth) return 'last';
  return (['first', 'second', 'third', 'fourth', 'last'] as const)[Math.min(4, Math.floor((date.getDate() - 1) / 7))] ?? 'last';
}

/** The rule without its end (never ends). */
export function withoutEnd(rule: Recurrence): Recurrence {
  const copy = { ...rule };
  delete copy.endDate;
  delete copy.count;
  return copy;
}

/** The rule anchored at the event start (start date follows the event when it moves). */
export const anchored = (rule: Recurrence, start: Date): Recurrence => ({ ...rule, startDate: isoDate(start) });

/** Editor choice → API recurrence, anchored at the event start. */
export function buildRecurrence(choice: RepeatChoice, start: Date, until: string): Recurrence | null {
  if (choice === 'none') return null;
  const base = { interval: 1, startDate: isoDate(start), ...(until && { endDate: until }) };
  switch (choice) {
    case 'daily':
      return { ...base, pattern: 'daily' };
    case 'weekdays':
      return { ...base, pattern: 'weekly', daysOfWeek: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'] };
    case 'weekly':
      return { ...base, pattern: 'weekly', daysOfWeek: [WEEKDAYS[start.getDay()] ?? 'monday'] };
    case 'monthly':
      return { ...base, pattern: 'monthly', dayOfMonth: start.getDate() };
    case 'yearly':
      return { ...base, pattern: 'yearly', dayOfMonth: start.getDate(), month: start.getMonth() + 1 };
  }
}

/** API recurrence → editor choice (`undefined`: a pattern the editor cannot show, e.g. "every 2nd Tuesday"). */
export function choiceOf(recurrence: Recurrence | null): RepeatChoice | undefined {
  if (!recurrence) return 'none';
  if (recurrence.interval !== 1 || recurrence.count) return undefined;
  if (recurrence.pattern === 'weekly') {
    const days = recurrence.daysOfWeek ?? [];
    if (days.length === 5 && !days.includes('saturday') && !days.includes('sunday')) return 'weekdays';
    return days.length === 1 ? 'weekly' : undefined;
  }
  return recurrence.pattern === 'other' ? undefined : recurrence.pattern;
}

/** "Every week on Monday, until 31 December 2026". */
export function recurrenceText(recurrence: Recurrence, t: Translate, format: CalendarFormat): string {
  const reference = new Date(2026, 0, 4); // a Sunday: weekday names via Intl
  const monthName = (month: number) => new Intl.DateTimeFormat(format.locale, { month: 'long' }).format(new Date(2026, month - 1, 1));
  const nthDay = (r: Recurrence) => (r.dayKind ? t(`webmail.calendarUx.recurrence.dayKind.${r.dayKind}`) : new Intl.DateTimeFormat(format.locale, { weekday: 'long' }).format(new Date(2026, 0, 4 + WEEKDAYS.indexOf(r.daysOfWeek?.[0] ?? 'monday'))));
  const dayName = (day: string) => format.weekday(new Date(reference.getFullYear(), 0, 4 + WEEKDAYS.indexOf(day as (typeof WEEKDAYS)[number])));
  const base =
    recurrence.pattern === 'daily'
      ? t('webmail.calendar.recurrence.daily', { n: recurrence.interval })
      : recurrence.pattern === 'weekly'
        ? t('webmail.calendar.recurrence.weekly', { n: recurrence.interval, days: format.list((recurrence.daysOfWeek ?? []).map(dayName)) })
        : recurrence.pattern === 'monthly'
          ? recurrence.weekIndex
            ? t('webmail.calendarUx.recurrence.monthlyNth', { n: recurrence.interval, index: t(`webmail.calendarUx.recurrence.index.${recurrence.weekIndex}`), day: nthDay(recurrence) })
            : t('webmail.calendar.recurrence.monthly', { n: recurrence.interval, day: recurrence.dayOfMonth ?? 1 })
          : recurrence.pattern === 'yearly'
            ? recurrence.weekIndex
              ? t('webmail.calendarUx.recurrence.yearlyNth', { index: t(`webmail.calendarUx.recurrence.index.${recurrence.weekIndex}`), day: nthDay(recurrence), month: monthName(recurrence.month ?? 1) })
              : recurrence.dayOfMonth
                ? t('webmail.calendarUx.recurrence.yearlyDate', { date: format.dayMonth(new Date(2026, (recurrence.month ?? 1) - 1, recurrence.dayOfMonth)) })
                : t('webmail.calendar.recurrence.yearly')
            : t('webmail.calendar.recurrence.other');
  if (recurrence.endDate) return t('webmail.calendar.recurrence.until', { rule: base, date: format.shortDay(new Date(`${recurrence.endDate}T00:00:00`)) });
  if (recurrence.count) return t('webmail.calendar.recurrence.count', { rule: base, n: recurrence.count });
  return base;
}

export type Frequency = 'none' | 'daily' | 'weekdays' | 'weekly' | 'monthly' | 'yearly';
export const WORKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'] as const;

/** A rule of the frequency anchored at `start` (Outlook's defaults: same weekday, same day of month). */
export function defaultRule(frequency: Frequency, start: Date): Recurrence | null {
  const base = { interval: 1, startDate: isoDate(start) };
  const day = WEEKDAYS[start.getDay()] ?? 'monday';
  switch (frequency) {
    case 'none':
      return null;
    case 'daily':
      return { ...base, pattern: 'daily' };
    case 'weekdays':
      return { ...base, pattern: 'weekly', daysOfWeek: [...WORKDAYS] };
    case 'weekly':
      return { ...base, pattern: 'weekly', daysOfWeek: [day] };
    case 'monthly':
      return { ...base, pattern: 'monthly', dayOfMonth: start.getDate() };
    case 'yearly':
      return { ...base, pattern: 'yearly', dayOfMonth: start.getDate(), month: start.getMonth() + 1 };
  }
}

/** Does a relative monthly/yearly rule ("2nd Tuesday", "last day", "last weekday") fall on `day`? (Preview mock.) */
export function matchesRelative(rule: Recurrence, day: Date): boolean {
  if (!rule.weekIndex) return false;
  if (rule.pattern === 'yearly' && day.getMonth() + 1 !== (rule.month ?? 1)) return false;
  const last = new Date(day.getFullYear(), day.getMonth() + 1, 0).getDate();
  const fits = (d: Date) => {
    const weekday = d.getDay();
    if (rule.dayKind === 'day') return true;
    if (rule.dayKind === 'weekday') return weekday > 0 && weekday < 6;
    if (rule.dayKind === 'weekendDay') return weekday === 0 || weekday === 6;
    return (rule.daysOfWeek ?? []).includes(WEEKDAYS[weekday] ?? 'monday');
  };
  const candidates = Array.from({ length: last }, (_, i) => new Date(day.getFullYear(), day.getMonth(), i + 1)).filter(fits);
  const pick = rule.weekIndex === 'last' ? candidates.at(-1) : candidates[['first', 'second', 'third', 'fourth'].indexOf(rule.weekIndex)];
  return pick?.getDate() === day.getDate();
}
